/**
 * The sources this app is willing to read, and the ones it refuses.
 *
 * Two rules the file is built around, both load-bearing:
 *
 *  - `door` is the literal 'corpus' and there is no other member. A retrieved
 *    record enters as a PassageCite (`resolveCites`, engine/reason.ts:1230), never
 *    as a WebSource, because `refutingDomains` (core/types.ts:1581) reads
 *    WebSource[] and nothing else — so nothing here can ever earn `refuted`, at any
 *    multiplicity. Widening this type is a change to invariant 2 and needs an issue
 *    and a decisions entry.
 *
 *  - `SourceCategory` has no 'news', 'markets' or 'social' member, ON PURPOSE.
 *    Wire-service and press-release syndication publishes ONE artefact on MANY
 *    registrable domains, which defeats the independence proxy in url.ts with no
 *    attacker. `refutingDomains` now collapses copies of one artefact before it
 *    counts hosts (D28), so two verbatim copies no longer clear the bar — but the
 *    refusal is UNCHANGED, because the other half of D23's argument never depended
 *    on syndication: `bbc.co.uk` and `bbc.com` are two registrable hosts for one
 *    newsroom, so two of its own articles, written separately, are two independent
 *    owners to this gate and always were. GDELT is an index OVER syndicated copies,
 *    so it is the worst case rather than the edge case. Adding such a row means
 *    widening this union first — read docs/decisions.md before you do.
 *
 * Five rows fetch, through ONE door, by TWO paths that differ in exactly one way
 * that matters. `engine/liveSources.ts` calls OpenAlex, Crossref, Europe PMC and
 * ClinicalTrials.gov at the moment a source check runs: the records come back as
 * EPHEMERAL corpus documents, join the same local BM25 pool as the user's own, and
 * are discarded with the pass. Nothing that gather fetches enters the corpus store,
 * so nothing it fetches spends the corpus budget — but the DOCUMENT is what is
 * ephemeral, not the CITE: a passage the check cited keeps its quote, title and
 * notice on `verification.corpus.cites` and is stored with the check. The Sources
 * note and docs/limits.md both said "never stored" until that was found in review;
 * say "not kept" here too, because the unqualified word is what made them wrong.
 * Europe PMC is the one that serves the SOURCE-CHECK half as well, and it needed
 * no second door to do it — a passage's quote is sliced by this client out of
 * text this client fetched, so it is quotable through the corpus door already
 * (D32). Federal Register alone stays false. `wired` stays false until the origin
 * is in public/_headers connect-src AND something calls it; csp.test.ts holds
 * that pair together.
 *
 * `engine/paper.ts` is the second path, and the arXiv row's only one. It fetches
 * ONE work, named by the user as a DOI or an arXiv id, and what it fetches IS
 * stored — through `addDoc`, against the corpus budget, refusable by every cap
 * `core/corpus.ts` keeps. That asymmetry is the right way round rather than an
 * inconsistency: the check-time gather is automatic and must cost a user nothing
 * they did not ask for, while a paper somebody typed the identifier of is a paste
 * they did not have to go and make by hand. It is still the corpus door, so a
 * passage of such a document is still a PassageCite and still cannot earn
 * `refuted`, at any multiplicity.
 *
 * Like `core/models.ts`, this file imports NOTHING at runtime, and for the same
 * reason: `models.ts` is the boot-cycle hazard, and a registry it could reach
 * would join that cycle. The dependency direction is
 * `ui/*, engine/*, core/markdown.ts, core/manifest.ts -> core/sources.ts` and
 * never the reverse — which is what lets `liveOrigin` and `liveOriginUrl` at the
 * foot of this file be read by a prompt, a React list and two exporters without
 * any of them importing the engine to find out where a passage came from, or how
 * to open it. `wellFormed` is here for the same reason read backwards: the engine
 * and this file need ONE lone-surrogate rule between them, and this is the only
 * side of that edge both can reach.
 */

export type SourceCategory = 'research' | 'registries' | 'policy'

/** Which of the two searching passes a row can actually serve. Metadata-only rows
 *  can do prior art and nothing else: `quoteFound` (reason.ts:789) needs real text. */
export type SourceJob = 'prior-art' | 'source-check'

export interface SourceDef {
  readonly id: string
  readonly name: string
  readonly category: SourceCategory
  /** One line: the question this row answers. Not a description of the service. */
  readonly blurb: string
  /** The ONE https origin this row may ever call. Mirrors a connect-src entry. */
  readonly origin: string
  /** Literal. A paid source cannot typecheck: a reasoning tool should not have a
   *  reading that only subscribers can reach. */
  readonly free: true
  /** Literal. Every adopted row is keyless, so there is no key store here at all —
   *  the only contact detail is `nv.src.mailto`, an address, not a secret. */
  readonly key: 'none'
  readonly defaultOn: boolean
  readonly serves: readonly SourceJob[]
  /** Literal — see the header. */
  readonly door: 'corpus'
  /** False until this origin appears in public/_headers connect-src AND something
   *  fetches it. The settings row renders "Not connected yet" while false. */
  readonly wired: boolean
}

/**
 * Six rows, every one of them keyless — which is why this whole surface adds
 * ZERO new secrets to the origin. A list that needed keys would have put seven
 * more of them beside `nv.key.anthropic`, turning one XSS from a two-key loss
 * into a nine-key loss. What replaces the screenshot's whole "API key required /
 * Get a key" column is a single non-secret contact address, `nv.src.mailto`.
 */
export const SOURCES: readonly SourceDef[] = [
  {
    id: 'openalex',
    name: 'OpenAlex',
    category: 'research',
    // Abstracts arrive as an INVERTED INDEX — word to positions — so any passage
    // built from one is a client-side RECONSTRUCTION and is lossy on punctuation.
    // Presented as the author's sentence it becomes a quote nobody wrote, which
    // is the one artefact the citation gate exists to keep off the canvas. So it
    // is marked: `RECONSTRUCTED_MARK` in engine/liveSources.ts goes into the doc
    // TITLE, which travels everywhere the cite is shown.
    blurb: 'Has anyone already published this? About 250 million works, no key.',
    origin: 'https://api.openalex.org',
    free: true,
    key: 'none',
    defaultOn: true,
    serves: ['prior-art'],
    door: 'corpus',
    wired: true,
  },
  {
    id: 'crossref',
    name: 'Crossref',
    category: 'research',
    // Adopt as a RESOLVER, not as a second search index. Used to FIND papers it
    // is largely redundant with OpenAlex, which is built on top of it; used to
    // resolve a DOI the model already named it is the only row here that REDUCES
    // the trust surface rather than adding one — it cannot introduce a claim, it
    // can only confirm or fail to confirm one. Its `update-to` relations are also
    // the only way a retraction becomes visible AS a retraction.
    blurb: 'Resolves a DOI the model named to a real record, including retractions.',
    origin: 'https://api.crossref.org',
    free: true,
    key: 'none',
    defaultOn: true,
    serves: ['prior-art'],
    door: 'corpus',
    wired: true,
  },
  {
    id: 'europepmc',
    name: 'Europe PMC',
    category: 'research',
    // The only adopted row that returns open-access FULL TEXT rather than
    // metadata, which is exactly the line between the two passes: a metadata-only
    // source can say a paper exists, and only this one can hand over the
    // paragraph a quote is checked against. Its abstracts are the author's own
    // text rather than an index this build reassembles, which is why it OUTRANKS
    // OpenAlex in the dedupe — see the adapter table in engine/liveSources.ts.
    // Silent outside biomedicine and the life sciences — which is harmless, and
    // why it is still default-on.
    blurb: 'Open-access full text, so a quote can actually be checked. Life sciences only.',
    origin: 'https://www.ebi.ac.uk',
    free: true,
    key: 'none',
    defaultOn: true,
    serves: ['prior-art', 'source-check'],
    door: 'corpus',
    wired: true,
  },
  {
    id: 'arxiv',
    name: 'arXiv',
    category: 'research',
    // A RESOLVER, never a search index — which is the whole of why this row is
    // admissible at all. docs/decisions.md rejects arXiv against the sources
    // survey, and that argument is about SEARCH: a preprint index queried
    // alongside OpenAlex returns a second copy of works OpenAlex already indexes,
    // side by side, looking like two sources agreeing. Nothing calls this row with
    // a query. It has no entry in `ADAPTERS` in engine/liveSources.ts and
    // `enabledLiveRows` will not hand an adapter-less row to a gather, so a source
    // check cannot reach it however the switches are set. What it answers is one
    // question about one work whose identifier the user typed: what is this paper
    // called, and what does its abstract say. Crossref was adopted on exactly that
    // distinction — a row that cannot introduce a claim, only confirm or fail to
    // confirm the one it was handed.
    //
    // METADATA ONLY, so it serves `prior-art` and not `source-check`: the export
    // API hands over a title, an author list and an abstract, and there is no full
    // text behind it for a quote to be checked against. The PDF beside every
    // preprint is not read here or anywhere.
    blurb: 'Resolves an arXiv id you type into its title and abstract. No full text.',
    origin: 'https://export.arxiv.org',
    free: true,
    key: 'none',
    // On, because the door it serves is a press somebody has already made: they
    // typed an identifier. The switch is not decorative — `fetchPaper` is handed
    // the rows the user has on and refuses an arXiv id when this one is off, which
    // is the only way a switch on a row no gather calls can mean anything.
    defaultOn: true,
    serves: ['prior-art'],
    door: 'corpus',
    wired: true,
  },
  {
    id: 'clinicaltrials',
    name: 'ClinicalTrials.gov',
    category: 'registries',
    // Answers "has this been tried" with a REGISTRATION RECORD rather than with
    // prose about one, and uniquely sees registered-but-unpublished trials — the
    // negative results publication bias hides. It is the one row that can tell an
    // `experiment` node its idea was tried and did not work.
    //
    // OFF BY DEFAULT, for the Federal Register's reason below: it answers claims
    // about treatments and interventions and is noise on everything else. A study
    // watched it asked about batteries and the Bronze Age on every check, and every
    // registration it returned joined the passages the model was offered. Somebody
    // whose claims are clinical turns it on under Settings → Sources → Registries.
    blurb:
      'Has this been tried? Registered trials, including the ones never published. Clinical claims only.',
    origin: 'https://clinicaltrials.gov',
    free: true,
    key: 'none',
    defaultOn: false,
    serves: ['prior-art'],
    door: 'corpus',
    wired: true,
  },
  {
    id: 'federalregister',
    name: 'Federal Register',
    category: 'policy',
    // Primary text of record: a claim about what a rule requires is settled by the
    // rule's own text, not by an article about the rule. OFF BY DEFAULT because it
    // adjudicates US-regulatory claims only and is silent on everything else — a
    // narrow, high-quality, keyless row is the precise case off-by-default is for.
    // Do not let it grow into GovInfo or Congress.gov; both are rejected in
    // docs/decisions.md, with reasons.
    blurb: "The rule's own text rather than an article about it. US regulations only.",
    origin: 'https://www.federalregister.gov',
    free: true,
    key: 'none',
    defaultOn: false,
    serves: ['prior-art', 'source-check'],
    door: 'corpus',
    wired: false,
  },
]

export const SOURCE_CATEGORIES: readonly SourceCategory[] = [
  'research',
  'registries',
  'policy',
] as const

/** Hoisted for the reason `LENS_LABEL` is: one vocabulary, so a group heading and
 *  a future filter cannot disagree about what a category is called. */
export const SOURCE_CATEGORY_LABEL: Record<SourceCategory, string> = {
  research: 'Research',
  registries: 'Registries',
  policy: 'Policy',
}

/* ── which passages are the user's, and which this app fetched ─────────────
   D27 merged the fetched records into ONE retrieval pool with the user's own
   documents, deliberately: one ranker, one coverage floor, one character budget.
   What the merge also did — found in review a round later — is erase the one
   thing D23 asked for beside the separate budget and the separate ceiling, a
   separate VISUAL CLASS. Downstream of the merge a fetched passage was offered
   to the model under "YOUR OWN SOURCES", listed in the inspector as "your own
   source", and exported as "(your own document)".

   The fact that tells them apart was there the whole time and needed no new
   field: the document's id. So it is read HERE, once, by everything that shows a
   passage to anybody — the prompt, the inspector, both markdown exports and the
   audit manifest — rather than by five surfaces each testing for a prefix of
   their own, which is five places to forget the day a sixth prefix is minted. */

/**
 * Where a passage came from, as the id of the row that fetched it — or `doi` for
 * a fetched record the id identifies by its DOI instead.
 *
 * Written out as a union rather than typed as `SourceDef['id']`, which is only
 * `string`: these are the values `liveOrigin` can return, a reader should be able
 * to see them without opening the adapter table, and `sources.test.ts` pins every
 * member but `doi` against a real row in `SOURCES` so the two cannot drift.
 *
 * `doi` is the member that names no row, and it is honest that way. Three of the
 * four fetching rows mint `doi:<the bare DOI>` for any record carrying one —
 * that is the whole point of it, since a shared identity is what collapses two
 * services' copies of one work into one document — so the id says the record was
 * fetched and does not say which index handed it over. Naming a row there would
 * be inventing provenance, which is the exact thing this helper exists to stop.
 */
export type PassageOrigin = 'openalex' | 'crossref' | 'europepmc' | 'clinicaltrials' | 'doi'

/**
 * The same string with nothing left in it that is half a character.
 *
 * `encodeURIComponent` is the whole reason this exists. It encodes CODE POINTS, so
 * a surrogate with no partner is not something it can express — it throws
 * `URIError` rather than guess — and TWO places in this tree run somebody else's
 * string through it.
 *
 *  - THE PRECONDITION of every url `engine/liveSources.ts` builds: a query, a DOI
 *    and a pmcid all come from the user or from a third party, and a url is built
 *    one expression before the request it is for, OUTSIDE `getBody`'s no-throw
 *    envelope. So a single half-character rejected the whole gather, from a module
 *    whose header promises a failure never throws out of it. Held where each string
 *    is MADE rather than at each `url()` — D13's shape, because a rule asserted at
 *    six encoding sites is a rule five of the copies can drift out of.
 *  - THE GUARD in `liveOriginUrl` below, which percent-encodes an identifier out of
 *    a STORED docId — third-party text that reached disk without passing any of
 *    those sites. There the answer is null rather than a sweep; see the note there.
 *
 * ONE definition, and it lives in the registry because there is nowhere else in
 * core it could: a module this one imported to get it would join the boot cycle the
 * header refuses, and a pure regex function adds no runtime import, so the rule can
 * sit here without costing that. `engine/liveSources.ts` imports it FROM here,
 * which is the legal direction.
 *
 * The pattern is the two ways a pair breaks: a high surrogate with no low after it,
 * a low with no high before it. Not `String.prototype.toWellFormed()`, which says
 * this in one call — it is ES2024 and this build targets ES2022, and it SUBSTITUTES
 * U+FFFD where this drops. A replacement character in a search term is a term the
 * user never typed, sent to a service as though they had; in a DOI it is a work
 * nobody can look up. Dropping what was never a character is the honest direction.
 */
export const wellFormed = (s: string): string =>
  s.replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '')

/**
 * A path segment, safe in a URL and safe in a markdown link destination.
 *
 * Per SEGMENT, because a DOI is `10.x/suffix` and those slashes are part of the
 * identifier: encoding the string whole would turn `/` into `%2F` and hand
 * doi.org a name nobody registered. Parentheses are then percent-encoded on top
 * of what `encodeURIComponent` leaves alone, for a reason outside the URL — a
 * bare `)` closes a markdown link destination early, and the SICI-shaped DOIs
 * publishers still mint (`10.1002/(SICI)1097-0258…`) carry two of them. Both
 * exporters put this in `[title](…)`, and a resolver reads `%28` as `(`.
 */
const pathSafe = (s: string): string =>
  s.split('/').map(encodeURIComponent).join('/').replace(/\(/g, '%28').replace(/\)/g, '%29')

/**
 * Europe PMC addresses a record by the DATABASE it is in and its id within it,
 * which is exactly what `epmc:<SOURCE>:<ID>` carries — `MED:24001454` and
 * `PPR:24001454` are two different records, as `liveSources.ts` says where it
 * mints them. Anything not carrying both halves has no address, so it gets none.
 */
const epmcUrl = (rest: string): string | null => {
  const at = rest.indexOf(':')
  const source = rest.slice(0, at)
  const id = rest.slice(at + 1)
  return at === -1 || !source || !id
    ? null
    : `https://europepmc.org/article/${pathSafe(source)}/${pathSafe(id)}`
}

/** Prefix → origin and the canonical public address of what that prefix
 *  identifies, in the shapes `engine/liveSources.ts` mints. Two of them do not
 *  spell their row's id — `epmc:` and `ct:` — which is why this is a table and
 *  not a split on the colon. ONE table rather than two, so a sixth prefix is one
 *  edit and cannot arrive with an origin but no door, or the reverse. */
const LIVE_DOC_PREFIX: readonly (readonly [
  string,
  PassageOrigin,
  (identifier: string) => string | null,
])[] = [
  ['doi:', 'doi', (doi) => `https://doi.org/${pathSafe(doi)}`],
  ['openalex:', 'openalex', (wid) => `https://openalex.org/${pathSafe(wid)}`],
  // NO DOOR, and the id says why. `crossref:` is minted ONLY for a record with no
  // DOI — one with a DOI is keyed `doi:` instead, which is what collapses two
  // indexes' copies of one work — so what follows this prefix is the head of the
  // record's TITLE, not an identifier. Crossref has no address for a title, and
  // guessing one from prose is precisely what the rest of this file refuses.
  ['crossref:', 'crossref', () => null],
  ['epmc:', 'europepmc', epmcUrl],
  ['ct:', 'clinicaltrials', (nct) => `https://clinicaltrials.gov/study/${pathSafe(nct)}`],
]

/** The codec's id cap, mirrored from `engine/liveSources.ts` — this file imports
 *  nothing at runtime, see the header, so the shape is written down here and
 *  `sources.test.ts` builds a real capped id to check the two agree. `capId`
 *  keeps 51 characters of an over-long id's HEAD and spends the last nine on `~`
 *  plus eight hex characters of a hash of the whole. */
const DOC_ID_CHARS = 60
const ID_HASH_CHARS = 8
const CAPPED_ID = new RegExp(
  `^[\\s\\S]{${DOC_ID_CHARS - ID_HASH_CHARS - 1}}~[0-9a-f]{${ID_HASH_CHARS}}$`,
)

/**
 * A fetched passage's origin, or null for a document the user added themselves.
 *
 * DERIVED, never stored. `PassageCite` gains no field for this, because the
 * `docId` it already stores is the fact — and a derived answer cannot go stale
 * against the record the way a second stored copy of it could. The id survives
 * everywhere the cite does: `capId` in `engine/liveSources.ts` keeps as much of
 * an over-long id's HEAD as fits beside a hash of the whole, so a prefix is never
 * what gets clipped, and `asPassageCite`'s own 60-character clamp is that same
 * cap, so nothing is lost on the way to disk either.
 *
 * A USER'S OWN ID CANNOT COLLIDE WITH THESE, structurally rather than by
 * convention. `addDoc` mints one through `uid('c')` in `core/id.ts`: the letter
 * `c`, an underscore, and two base-36 numbers — an alphabet of `[0-9a-z_]`. Every
 * prefix above ends in a colon, which that alphabet does not contain, so no
 * document anybody pastes can be read as fetched whatever they title it and
 * whatever is in it. (`core/id.ts` is not imported here — this file imports
 * nothing at runtime, see the header — so the argument is written down and
 * `sources.test.ts` mints real ids to check it.)
 *
 * A prefix test rather than a registry lookup, deliberately: an id is a value in
 * a stored record, and a record written last year must not need a registry that
 * has since changed in order to say where it came from.
 */
export function liveOrigin(docId: string): PassageOrigin | null {
  for (const [prefix, origin] of LIVE_DOC_PREFIX) if (docId.startsWith(prefix)) return origin
  return null
}

/**
 * The public address of a fetched record, or null where there is no door.
 *
 * THE ROAD, not the gate. A cite from a fetched record carried its title, its
 * quote and — since `liveOrigin` — the name of the row it came from, all of which
 * a reader could quote and none of which they could WALK: no surface printed a
 * link, so the one thing a reader wants from a citation, the source itself, was
 * the one thing the record would not give them. That is a missing road and not a
 * hole in the citation gate: a passage is admitted by resolving against the table
 * the prompt printed and its quote is sliced by this client out of bytes this
 * client fetched (`resolveCites` in engine/reason.ts, D27), which is a stronger
 * guarantee than the web side's and is untouched by anything here. Invariant 1
 * governs the URL door; this is the corpus door, and it stays a separate path.
 *
 * DERIVED FROM THE ID AND NOTHING ELSE, like `liveOrigin` and for its reasons,
 * with one of its own: a URL built from a TITLE or a QUOTE would be a guess at
 * where somebody else's words live, and a citation pointing at a guess is the
 * artefact this whole surface exists to make impossible. Nothing is stored, so
 * `PassageCite`, `LoadedDoc` and the codec are untouched — and every cite already
 * on disk gains its link the moment it is next rendered, because the fact the
 * link is derived from was written down at the time of the check.
 *
 * FIVE WAYS TO GET NULL, and a surface renders all five the same way: the title
 * and the origin, printed as they already are, with nothing to click.
 *
 *  - A document the user added. Structurally impossible to mistake for a fetched
 *    one — see `liveOrigin`: `uid('c')` mints from `[0-9a-z_]` and every prefix
 *    ends in a colon.
 *  - `crossref:`, whose identifier half is a title fragment. See the table.
 *  - A malformed `epmc:` id, missing the database or the id within it.
 *  - A CAPPED id. `capId` spends an over-long id's tail on a hash, so the
 *    identifier is no longer whole and any address built from it would point at
 *    another record or at nothing. Accepted cost: a record whose id ran past 60
 *    characters — a long DOI — keeps its name and loses its link, which is the
 *    right direction to fail in, because the alternative is a link that 404s
 *    under a title claiming it resolves. A genuine 60-character id ending in `~`
 *    and eight hex characters is read as capped and loses its link too; it costs
 *    the same link and nothing else.
 *  - An id that is not WELL-FORMED UTF-16. A stored docId is third-party text: the
 *    adapters carry OpenAlex's `id`, Europe PMC's `source` and id, a `nctId` and a
 *    Crossref title fragment through `txt`, which sweeps no surrogates, and
 *    `JSON.parse` keeps a hostile `\uD800` escape as one. `encodeURIComponent`
 *    throws `URIError` on half a character, so the derivation this whole comment
 *    describes could take down a render or an export.
 *
 * NULL RATHER THAN A SWEEP, and the asymmetry with `engine/liveSources.ts` is the
 * point rather than an inconsistency. There the swept string is about to LEAVE the
 * machine as a search term or a filter, where a partial term still searches and the
 * alternative is losing the whole gather. Here the string claims to be an EXACT
 * identifier: strip the half-character out of `openalex:W27418\uD8009807` and what
 * is left is a plausible-looking id for a different record or for none, so the link
 * would open the wrong door under the right name. That is strictly worse than no
 * door — the capped-id arm's reasoning, one line up, for the same reason.
 *
 * `doi:` is the complement of `liveOriginName`'s null: the id names no ROW, so
 * nothing can say which index handed the record over, and it names a WORK
 * exactly, so doi.org resolves it. A reader gets the door without being told a
 * service that may not have been the one.
 */
export function liveOriginUrl(docId: string): string | null {
  // The WHOLE id, prefix and identifier alike, before anything reads either: the
  // sweep changing nothing is exactly what "well-formed" means, so this is the one
  // definition above asked as a question rather than a second copy of its pattern.
  // A malformed id fails here instead of throwing `URIError` out of `pathSafe`,
  // three frames down and inside a React render.
  if (wellFormed(docId) !== docId) return null
  // Before the prefix walk, not after: a capped id keeps its prefix — that is
  // what `capId` is careful about — so `liveOrigin` still reads it, and only the
  // identifier behind the prefix is gone.
  if (CAPPED_ID.test(docId)) return null
  for (const [prefix, , door] of LIVE_DOC_PREFIX) {
    if (!docId.startsWith(prefix)) continue
    const identifier = docId.slice(prefix.length)
    return identifier ? door(identifier) : null
  }
  return null
}

/**
 * What a reader is told that origin IS — the registry row's own display name, so
 * renaming a row above renames it in the prompt, the inspector and both exports
 * at once.
 *
 * Null for `doi`, and every caller has to answer for it: there is no row to name,
 * so a surface says the passage was fetched and stops there rather than printing
 * a plausible service beside it.
 */
export const liveOriginName = (o: PassageOrigin): string | null =>
  SOURCES.find((s) => s.id === o)?.name ?? null
